/** Internal staff (admin) accounts must use this email domain. */
export const STAFF_EMAIL_DOMAIN = 'wa-pharma.com';

export function normalizeStaffEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function isStaffEmailDomain(
  email: string,
  domain: string = STAFF_EMAIL_DOMAIN,
): boolean {
  const normalized = normalizeStaffEmail(email);
  const at = normalized.lastIndexOf('@');
  if (at < 0) return false;
  return normalized.slice(at + 1) === domain.trim().toLowerCase();
}

/** French validation message for admin/staff flows; `null` when allowed. */
export const STAFF_EMAIL_DOMAIN_REJECTED_MESSAGE =
  'Cette adresse e-mail n’est pas autorisée.';

export function getStaffEmailDomainValidationError(
  email: string,
  domain: string = STAFF_EMAIL_DOMAIN,
): string | null {
  if (isStaffEmailDomain(email, domain)) return null;
  return STAFF_EMAIL_DOMAIN_REJECTED_MESSAGE;
}

export type CanAccessAdminAppInput = {
  email: string;
  roles?: readonly string[] | null;
  packs?: readonly string[] | null;
};

/**
 * Outer admin-app entry: staff email domain + at least one role or pack.
 * Does not encode seeded job names — custom roles with packs may enter.
 */
export function canAccessAdminApp(
  input: CanAccessAdminAppInput,
  domain: string = STAFF_EMAIL_DOMAIN,
): boolean {
  if (!isStaffEmailDomain(input.email, domain)) return false;
  const roles = (input.roles ?? []).map((r) => r.trim()).filter(Boolean);
  const packs = (input.packs ?? []).map((p) => p.trim()).filter(Boolean);
  return roles.length > 0 || packs.length > 0;
}

/** Default bootstrap email from `pnpm db:seed` when `ADMIN_SEED_EMAIL` unset. */
export const DEFAULT_ADMIN_SEED_EMAIL = 'admin@wa-pharma.com';

export type CanAccessInitialSetupInput = {
  roles?: readonly string[] | null;
  /** True when account email matches `ADMIN_SEED_EMAIL`. */
  bootstrapAdmin?: boolean | null;
  foundationsComplete?: boolean | null;
};

/**
 * Configuration initiale (SetupWizard): seed SUPER_ADMIN only, until foundations done.
 * Paramètres reopen allowed for that account while foundations incomplete.
 */
export function canAccessInitialSetup(input: CanAccessInitialSetupInput): boolean {
  if (!input.bootstrapAdmin) return false;
  if (input.foundationsComplete) return false;
  return (input.roles ?? []).some((r) => r.trim() === 'SUPER_ADMIN');
}

export function isBootstrapAdminEmail(
  email: string,
  seedEmail: string = DEFAULT_ADMIN_SEED_EMAIL,
): boolean {
  return normalizeStaffEmail(email) === normalizeStaffEmail(seedEmail);
}
